import { Row, Col, Form, Select, message, Card, Typography, Button, Spin, Divider, Tag, Space, Empty } from "antd";
import { AppPageHeader } from "components/AppPageHeader";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect } from "react";
import styled from "styled-components";
import { MergeOutlined, EnvironmentOutlined, HomeOutlined } from "@ant-design/icons";
import { variables } from "common/variables";
import { useMergeSociety } from "hooks/useMergeSociety";
import { MergeConfirmModal } from "./component/MergeConfirmModal";

const { Text } = Typography;
const MESSAGE_KEY = "merge_society";

const StyledMainCard = styled(Card)`
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  margin-top: 24px;

  .ant-card-head {
    background: ${variables.primaryColor};
    border-bottom: none;
    padding: 16px 24px;

    .ant-card-head-title {
      color: white;
      font-size: 16px;
      font-weight: 600;
    }
  }

  .ant-card-body {
    padding: 32px;
  }
`;

const StyledFormCard = styled(Card)`
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  background: white;

  .ant-card-body {
    padding: 28px;
  }
`;


const StyledFormLabel = styled.label`
  font-weight: 600;
  color: #262626;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const StyledMergeButton = styled(Button)`
  &.ant-btn-primary {
    background: ${variables.primaryColor};
    border: none;
    height: 40px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 6px;

    &:hover:not(:disabled) {
      background: ${variables.primaryColor};
      opacity: 0.85;
    }

    &:disabled {
      background: #d9d9d9 !important;
      border-color: #d9d9d9 !important;
      color: rgba(0, 0, 0, 0.25) !important;
      cursor: not-allowed;
    }
  }
`;

const StyledHelper = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
  line-height: 1.5;
`;

const MergeSocietyPage = () => {
  useDocumentTitle("Merge Society");

  const {
    darkStores,
    status,
    error,
    selectedStoreId,
    childSocietyId,
    parentSocietyId,
    form,
    showConfirmDialog,
    isLoadingChildSocieties,
    isLoadingParentSocieties,
    childSocietiesOptions,
    parentSocietiesOptions,
    childAddressCount,
    parentAddressCount,
    isLoadingChildAddressCount,
    isLoadingParentAddressCount,
    isMergeDisabled,
    setSelectedStoreId,
    handleChildSocietySearch,
    handleParentSocietySearch,
    handleChildSocietyChange,
    handleParentSocietyChange,
    handleMerge,
    performMerge,
    handleConfirmDialogCancel,
  } = useMergeSociety();

  // Handle status messages
  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading", key: MESSAGE_KEY });
    } else if (status === "saving") {
      message.loading({ content: "Merging societies...", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({ content: "Societies merged successfully!", key: MESSAGE_KEY });
      // Clear fields on success
      form.resetFields();
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error, form]);

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Merge Society" />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: "12px" }}>
        <Col xs={24} sm={24} md={18} lg={14}>
          {/* Store Selector */}
          <Card style={{ marginBottom: "24px", borderRadius: "8px", border: "none", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)" }}>
            <Form layout="vertical">
              <Form.Item label="Select Store" name="storeId" required>
                <Select
                  showSearch
                  placeholder="Select store"
                  value={selectedStoreId}
                  onChange={setSelectedStoreId}
                  filterOption={(input, option) => {
                    return option?.children?.toString()
                      .toLowerCase()
                      .includes(input?.toLowerCase() ?? "") ?? false;
                  }}
                >
                  {(darkStores ?? []).map((store) => (
                    <Select.Option value={store.id} key={store.id}>
                      {store.name} || {store.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Card>

          {/* Merge Society Form - Only show if store is selected */}
          {selectedStoreId && (
            <StyledMainCard>
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <StyledFormCard>
                  <Form form={form} layout="vertical" requiredMark={false}>
                    {/* Child Society Section */}
                    <div style={{ marginBottom: "32px" }}>
                      <StyledFormLabel>
                        <HomeOutlined style={{ fontSize: "16px", color: variables.primaryColor }} />
                        Child Society (Source)
                      </StyledFormLabel>
                      {childSocietyId && !isLoadingChildAddressCount && childAddressCount !== undefined && (
                        <StyledHelper style={{ color: variables.primaryColor, fontWeight: "500" }}>
                          Active Addresses: <strong>{childAddressCount}</strong>
                        </StyledHelper>
                      )}
                      {isLoadingChildAddressCount && (
                        <StyledHelper style={{ color: variables.primaryColor }}>
                          Loading address count...
                        </StyledHelper>
                      )}

                      <Form.Item
                        name="childSociety"
                        rules={[{ required: true, message: "Child society is required" }]}
                        style={{ marginTop: "12px", marginBottom: "0" }}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Search by society name or ID..."
                          onSearch={handleChildSocietySearch}
                          onChange={handleChildSocietyChange}
                          notFoundContent={isLoadingChildSocieties ? <Spin size="small" /> : <Empty description="No societies found" />}
                          loading={isLoadingChildSocieties}
                          optionLabelProp="label"
                          size="large"
                          style={{ borderRadius: "6px" }}
                          filterOption={false}
                          listHeight={400}
                          maxTagCount="responsive"
                        >
                          {childSocietiesOptions && childSocietiesOptions.length > 0 ? (
                            childSocietiesOptions.map((society) => (
                              <Select.Option key={`child-${society.id}`} value={society.id} label={society.name}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span>{society.name}</span>
                                  <Tag color="blue">ID: {society.id}</Tag>
                                </div>
                              </Select.Option>
                            ))
                          ) : (
                            <Select.Option disabled value="">
                              No options available
                            </Select.Option>
                          )}
                        </Select>
                      </Form.Item>

                      {childSocietyId && (
                        <div style={{ padding: "8px 0", marginTop: "8px" }}>
                          <Text style={{ color: "#d32f2f", fontSize: "12px", fontWeight: "500", display: "block", lineHeight: "1.5" }}>
                            Important: All the addresses data will be moved to the targeted society once merged
                          </Text>
                        </div>
                      )}
                    </div>

                    <Divider style={{ margin: "24px 0", borderColor: "#e8e8e8" }} />

                    {/* Parent Society Section */}
                    <div style={{ marginBottom: "32px" }}>
                      <StyledFormLabel>
                        <EnvironmentOutlined style={{ fontSize: "16px", color: variables.primaryColor }} />
                        Parent Society (Target)
                      </StyledFormLabel>
                      {parentSocietyId && !isLoadingParentAddressCount && parentAddressCount !== undefined && (
                        <StyledHelper style={{ color: variables.primaryColor, fontWeight: "500" }}>
                          Active Addresses: <strong>{parentAddressCount}</strong>
                        </StyledHelper>
                      )}
                      {isLoadingParentAddressCount && (
                        <StyledHelper style={{ color: variables.primaryColor }}>
                          Loading address count...
                        </StyledHelper>
                      )}

                      <Form.Item
                        name="parentSociety"
                        rules={[{ required: true, message: "Parent society is required" }]}
                        style={{ marginTop: "12px", marginBottom: "0" }}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Search by society name or ID..."
                          onSearch={handleParentSocietySearch}
                          onChange={handleParentSocietyChange}
                          disabled={!childSocietyId}
                          notFoundContent={isLoadingParentSocieties ? <Spin size="small" /> : <Empty description="Select child society first" />}
                          loading={isLoadingParentSocieties}
                          optionLabelProp="label"
                          size="large"
                          style={{ borderRadius: "6px" }}
                          filterOption={false}
                          listHeight={400}
                          maxTagCount="responsive"
                        >
                          {parentSocietiesOptions && parentSocietiesOptions.length > 0 ? (
                            parentSocietiesOptions.map((society) => (
                              <Select.Option
                                key={`parent-${society.id}`}
                                value={society.id}
                                disabled={society.id === childSocietyId}
                                label={society.name}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span>{society.name}</span>
                                  <Tag color="purple">ID: {society.id}</Tag>
                                </div>
                              </Select.Option>
                            ))
                          ) : (
                            <Select.Option disabled value="">
                              No options available
                            </Select.Option>
                          )}
                        </Select>
                      </Form.Item>
                    </div>

                    <Divider style={{ margin: "24px 0", borderColor: "#e8e8e8" }} />

                    {/* Merge Button */}
                    <Form.Item style={{ marginBottom: "0", marginTop: "24px" }}>
                      <Space direction="vertical" style={{ width: "100%" }} size="large">
                        <StyledMergeButton
                          type="primary"
                          onClick={handleMerge}
                          loading={status === "saving"}
                          disabled={isMergeDisabled}
                          block
                          icon={<MergeOutlined />}
                          size="large"
                        >
                          {isLoadingChildAddressCount ? "Checking affected addresses..." : "Merge Societies"}
                        </StyledMergeButton>

                        {childSocietyId && parentSocietyId && childAddressCount !== undefined && (
                          <div style={{ padding: "12px 16px", background: "#ffebee", borderRadius: "6px", border: "1px solid #ffcdd2", marginTop: "12px" }}>
                            <Text style={{ fontSize: "13px", display: "block", fontWeight: "600", color: "#d32f2f" }}>
                              <strong>Active Addresses to be Affected:</strong> {childAddressCount} address{childAddressCount !== 1 ? "es" : ""}
                            </Text>
                          </div>
                        )}
                      </Space>
                    </Form.Item>
                  </Form>
                </StyledFormCard>
              </Col>
            </Row>
            </StyledMainCard>
          )}
        </Col>
      </Row>

      {/* Merge Confirmation Modal */}
      <MergeConfirmModal
        open={showConfirmDialog}
        status={status}
        childAddressCount={childAddressCount}
        childSocietyName={childSocietiesOptions.find(s => s.id === childSocietyId)?.name || "Unknown"}
        parentSocietyName={parentSocietiesOptions.find(s => s.id === parentSocietyId)?.name || "Unknown"}
        onCancel={handleConfirmDialogCancel}
        onConfirm={performMerge}
      />

    </>
  );
};

export default MergeSocietyPage;
